import { FunctionComponent, useCallback } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { Customer } from '@/models/customer';
import { useDeleteCustomerMutation } from '@/api/v2/customer';

type DeleteCustomerConfirmationProps = {
    open: boolean;
    customer: Customer | null;
    onSuccess: () => void;
    onError: (message: string) => void;
    onCancel: () => void;
};

export const DeleteCustomerConfirmation: FunctionComponent<
    DeleteCustomerConfirmationProps
> = ({ open, customer, onSuccess, onError, onCancel }) => {
    const [deleteCustomer, { isLoading: isDeleting }] =
        useDeleteCustomerMutation();

    const onConfirm = useCallback(async () => {
        if (!customer) return;

        try {
            await deleteCustomer({ id: customer.id }).unwrap();
            onSuccess();
        } catch {
            onError('Fehler beim Löschen des Kunden');
        }
    }, [customer, deleteCustomer, onSuccess, onError]);

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby='delete-dialog-title'
            aria-describedby='delete-dialog-description'
        >
            <DialogTitle id='delete-dialog-title'>Kunde löschen</DialogTitle>
            <DialogContent>
                <DialogContentText id='delete-dialog-description'>
                    Sind Sie sicher, dass Sie den Kunden "{customer?.firstname}{' '}
                    {customer?.lastname}" löschen möchten? Diese Aktion kann
                    nicht rückgängig gemacht werden.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color='inherit'>
                    Abbrechen
                </Button>
                <Button
                    onClick={onConfirm}
                    color='error'
                    variant='contained'
                    disabled={isDeleting}
                >
                    {isDeleting ? 'Löschen...' : 'Löschen'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
